// [S-410] Minimal, safe Markdown → sanitized HTML for chat assistant output.
//
// The agent streams plain Markdown (LLMs emit **bold**, lists, `code`, etc.).
// assistant-ui renders text parts verbatim, so without this the raw markup
// shows through. We convert a deliberately small subset — headings, bold,
// italic, inline + fenced code, links, ordered/unordered lists, blockquotes,
// paragraphs — then run DOMPurify as defense-in-depth (mirrors how
// SourceViewer.tsx renders mammoth HTML). Input is HTML-escaped *before* any
// markup is introduced, so user/agent text can never inject tags.
import createDOMPurify from "dompurify";

// In the browser bundle the default export is already a window-bound instance
// (with `.sanitize`); under jsdom/node ESM it's the factory and must be bound
// to `window` first. Handle both so the renderer works in the app and in tests.
const DOMPurify =
  "sanitize" in createDOMPurify
    ? createDOMPurify
    : (createDOMPurify as unknown as (w: typeof window) => typeof createDOMPurify)(
        window
      );

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Inline spans. `s` is already HTML-escaped. Inline code is extracted first so
// its contents aren't re-interpreted as bold/italic/links.
function inline(s: string): string {
  const code: string[] = [];
  let out = s.replace(/`([^`]+)`/g, (_m, c: string) => {
    code.push(`<code>${c}</code>`);
    return ` ${code.length - 1} `;
  });
  // [text](url) — only http(s)/mailto survive; anything else points nowhere.
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, text: string, url: string) => {
      const safe = /^(https?:|mailto:)/i.test(url) ? url : "#";
      return `<a href="${safe}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    }
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_\w])_([^_\n]+)_/g, "$1<em>$2</em>");
  return out.replace(/ (\d+) /g, (_m, i: string) => code[Number(i)]!);
}

export function markdownToHtml(src: string): string {
  const lines = escapeHtml(src.replace(/\r\n/g, "\n")).split("\n");
  const blocks: string[] = [];
  let para: string[] = [];
  let i = 0;

  const flushPara = (): void => {
    if (para.length > 0) {
      blocks.push(`<p>${inline(para.join("\n")).replace(/\n/g, "<br/>")}</p>`);
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i]!;

    if (/^```/.test(line)) {
      flushPara();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i]!)) code.push(lines[i++]!);
      i++; // closing fence
      blocks.push(`<pre><code>${code.join("\n")}</code></pre>`);
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushPara();
      const level = heading[1]!.length;
      blocks.push(`<h${level}>${inline(heading[2]!)}</h${level}>`);
      i++;
      continue;
    }

    if (/^>\s?/.test(line)) {
      flushPara();
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i]!))
        quote.push(lines[i++]!.replace(/^>\s?/, ""));
      blocks.push(
        `<blockquote>${inline(quote.join("\n")).replace(/\n/g, "<br/>")}</blockquote>`
      );
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i]!))
        items.push(`<li>${inline(lines[i++]!.replace(/^\s*[-*+]\s+/, ""))}</li>`);
      blocks.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i]!))
        items.push(`<li>${inline(lines[i++]!.replace(/^\s*\d+\.\s+/, ""))}</li>`);
      blocks.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    if (line.trim() === "") {
      flushPara();
      i++;
      continue;
    }

    para.push(line);
    i++;
  }
  flushPara();

  return DOMPurify.sanitize(blocks.join("\n"), {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "code", "pre", "ul", "ol", "li", "a",
      "blockquote", "h1", "h2", "h3", "h4", "h5", "h6",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
}
